import { useQuery } from "@tanstack/react-query";
import { apiGet, apiGetBlob } from "./client";
import { useTicketSize, type TicketSize } from "../ticket-size";

/** Same sentinel role as WeightedAverageLife's own ALL_RATINGS_LABEL --
 *  omitted from the request entirely rather than sent as a literal filter
 *  value, since the backend has no "All Ratings" row to match against. */
export const ALL_RATINGS_LABEL = "All Ratings";

export interface AnnualizedLossTrendPoint {
  fileDate: string;
  lifetimeLoss: number;
  annualizedLossPct: number;
}

/** Leaf level -- one real industry under one (rating, office) pair. */
export interface AnnualizedLossIndustryRow {
  key: string;
  label: string;
  maturedContracts: number;
  financedAmount: number;
  lifetimeLoss: number;
  /** null only when financedAmount/pool factor is zero for this row --
   *  nothing to divide by, not a fabricated 0. */
  walYears: number | null;
  loanToLifeLossPct: number | null;
  annualizedLossPct: number | null;
}

/** Second level -- one real office under one rating, with its own industry
 *  breakdown. */
export interface AnnualizedLossOfficeRow extends AnnualizedLossIndustryRow {
  industries: AnnualizedLossIndustryRow[];
}

/** Top level -- one real bankdwn_risk_rating_final value (A/B1/B2/B3/Other),
 *  or the combined "All Ratings" row, with its own office breakdown. See the
 *  backend's own module docstring for why this is bankdwn_risk_rating_final
 *  and not lo_risk_rating. */
export interface AnnualizedLossRow extends AnnualizedLossIndustryRow {
  offices: AnnualizedLossOfficeRow[];
}

/** Leaf level of the Industry-led tree -- one real office under one
 *  (industry, rating) pair. */
export interface AnnualizedLossOfficeLeafRow extends AnnualizedLossIndustryRow {}

/** Second level of the Industry-led tree -- one real rating under one
 *  industry, with its own office breakdown. */
export interface AnnualizedLossRatingGroupRow extends AnnualizedLossIndustryRow {
  offices: AnnualizedLossOfficeLeafRow[];
}

/** Top level of the Industry-led tree -- one real sic_division value, or the
 *  combined "All Industries" row, with its own rating breakdown. Same
 *  underlying rows as AnnualizedLossRow, just regrouped Industry -> Rating ->
 *  Office instead of Rating -> Office -> Industry. */
export interface AnnualizedLossIndustryGroupRow extends AnnualizedLossIndustryRow {
  ratings: AnnualizedLossRatingGroupRow[];
}

export interface AnnualizedLossResponse {
  trend: AnnualizedLossTrendPoint[];
  rows: AnnualizedLossRow[];
  rowsByIndustry: AnnualizedLossIndustryGroupRow[];
}

/** Streams every contract behind the two grouped tables (one row per
 *  contract) under the page's current Industry / Office / Rating filters.
 *  CORS hides Content-Disposition cross-origin, so the filename is fixed here. */
export async function downloadAnnualizedLossContracts(
  industry?: string,
  officeNumber?: number,
  rating?: string,
  ticket?: TicketSize,
): Promise<void> {
  const blob = await apiGetBlob("/calibration/annualized-loss/download/contracts", {
    ticket,
    industry,
    office: officeNumber != null ? String(officeNumber) : undefined,
    rating: rating && rating !== ALL_RATINGS_LABEL ? rating : undefined,
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "annualized_loss_contracts.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** `officeNumber` (not office name) matches Weighted Average Life's own
 *  filter convention -- the page resolves the selected office name to its
 *  number before calling this. */
export function useAnnualizedLoss(industry?: string, officeNumber?: number, rating?: string) {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: [
      "annualized-loss",
      industry ?? "all",
      officeNumber ?? "all",
      rating ?? ALL_RATINGS_LABEL,
      ticket ?? "all",
    ],
    queryFn: () =>
      apiGet<AnnualizedLossResponse>("/calibration/annualized-loss", {
        ...(industry ? { industry } : {}),
        ...(officeNumber != null ? { office: String(officeNumber) } : {}),
        ...(rating && rating !== ALL_RATINGS_LABEL ? { rating } : {}),
        ...(ticket ? { ticket } : {}),
      }),
    staleTime: 60_000,
  });
}
